import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc/client";
import { useMerchant } from "@/hooks/useMerchant";

/**
 * The dashboard reads from two procedures with different lifetimes.
 *
 * `monthly` follows the period picker. `current` describes the portfolio as it
 * stands today — arrears, priorities and money in transit — and deliberately
 * ignores the picker, so browsing months never refetches it.
 */

const STALE_TIME = 5 * 60_000;

export const useAnalyticsMonthly = (yearMonth: string | undefined) => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();

  return useQuery({
    ...trpc.analytics.monthly.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
      yearMonth: yearMonth ?? "",
    }),
    enabled: !!merchant?.id && !!yearMonth,
    staleTime: STALE_TIME,
  });
};

export const useAnalyticsCurrent = () => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();

  return useQuery({
    ...trpc.analytics.current.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
    }),
    enabled: !!merchant?.id,
    staleTime: STALE_TIME,
  });
};

/**
 * Tenants behind one segment of the segments chart. Fetched only while that
 * segment is open — the classified population reaches five figures on the
 * largest merchants, so it never rides along with the chart itself.
 */
export const useAnalyticsSegmentCustomers = (
  yearMonth: string | undefined,
  segment: string | null
) => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();

  return useQuery({
    ...trpc.analytics.segmentCustomers.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
      yearMonth: yearMonth ?? "",
      segment: (segment ?? "puntual") as
        | "puntual"
        | "tardio"
        | "variable"
        | "reincidente",
    }),
    enabled: !!merchant?.id && !!yearMonth && !!segment,
    staleTime: STALE_TIME,
  });
};

export const useRegisterFianzaInterest = () => {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const register = useMutation(
    trpc.analytics.notifyFianzaInterest.mutationOptions()
  );

  return () => {
    if (merchant?.id) register.mutate({ rentalsMerchantId: merchant.id });
  };
};
